import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, UsersThree, ShieldCheck, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Admin · Bulk reassign reports_to (manager succession).
 * Tier 4+ only.
 */
export default function BulkReassign() {
  const { user: me } = useAuth();
  const isPriv = (me?.ux_tier || 0) >= 4;
  const [team, setTeam] = useState([]);
  const [fromUser, setFromUser] = useState("");
  const [toUser, setToUser] = useState("");
  const [preview, setPreview] = useState(null);
  const [executed, setExecuted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!isPriv) return;
    api.get("/team").then(r => setTeam((r.data || []).filter(u => u.active !== false))).catch(() => setTeam([]));
  }, [isPriv]);

  const candidates = useMemo(
    () => team.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [team]
  );

  const reset = () => { setPreview(null); setExecuted(null); setErr(null); };

  const runPreview = async () => {
    if (!fromUser) { setErr("Pick a departing manager first."); return; }
    setBusy(true); reset();
    try {
      const { data } = await api.post("/admin/bulk-reassign/preview", {
        from_user_id: fromUser,
        to_user_id: toUser || null,
      });
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Preview failed");
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Reassign ${preview.direct_reports_count} direct report(s) from ${preview.from_user?.name} → ${preview.to_user?.name || "(no manager)"}\n\n` +
      `Cascade impact:\n` +
      `• ${preview.pending_leaves_to_reroute} pending leave request(s) will reroute\n` +
      `• ${preview.open_cases_to_restamp} open case(s) will be re-stamped with refreshed RM/MD\n\n` +
      `This is logged in audit_log + employment-history. Proceed?`
    )) return;

    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/admin/bulk-reassign/execute", {
        from_user_id: fromUser,
        to_user_id: toUser || null,
        confirmation: "CONFIRM-REASSIGN",
      });
      setExecuted(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Execute failed");
    } finally { setBusy(false); }
  };

  if (!isPriv) {
    return (
      <div className="p-6" data-testid="bulk-reassign-gate">
        <div className="card-flat p-6 border border-amber-200 bg-amber-50 text-amber-900 text-[13px]">
          Tier 4+ (COO/CEO) required to access bulk-reassign tools.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="bulk-reassign-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Admin · Org succession</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Bulk reassign reports</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">
            Pick a departing manager and a successor — every direct report migrates in one step with full cascade propagation (pending leaves rerouted · open cases re-stamped · employment history written).
          </div>
        </div>
      </div>

      <section className="card-flat p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
          <Picker
            label="Departing manager"
            value={fromUser}
            options={candidates}
            onChange={v => { setFromUser(v); reset(); }}
            testid="bulk-reassign-from"
            placeholder="— pick a user —"
          />
          <div className="flex justify-center pb-2">
            <ArrowRight size={22} className="text-[var(--brand-primary)]" />
          </div>
          <Picker
            label="Successor (or leave blank = no manager)"
            value={toUser}
            options={candidates.filter(u => u.user_id !== fromUser)}
            onChange={v => { setToUser(v); reset(); }}
            testid="bulk-reassign-to"
            placeholder="(no manager)"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={runPreview}
            disabled={!fromUser || busy}
            data-testid="bulk-reassign-preview"
            className="span-btn h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <UsersThree size={13} /> Preview impact
          </button>
        </div>

        {err && (
          <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="bulk-reassign-error">
            {String(err)}
          </div>
        )}
      </section>

      {preview && (
        <section className="card-flat p-5 space-y-4" data-testid="bulk-reassign-preview-pane">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Tile label="Direct reports" value={preview.direct_reports_count} testid="tile-reports" />
            <Tile label="Pending leaves" value={preview.pending_leaves_to_reroute} testid="tile-leaves" />
            <Tile label="Open cases" value={preview.open_cases_to_restamp} testid="tile-cases" />
            <Tile label="Confirmation token" value={preview.confirmation_required} testid="tile-token" mono />
          </div>

          {preview.direct_reports_count === 0 ? (
            <div className="text-[12px] text-[var(--text-muted)] bg-[var(--surface-2)] p-3 rounded border border-[var(--border)]" data-testid="bulk-reassign-noop">
              <Warning size={13} className="inline mr-1" />
              {preview.from_user?.name} has no direct reports. Nothing to reassign.
            </div>
          ) : (
            <div data-testid="bulk-reassign-reports-list">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Reports that will migrate</div>
              <ul className="border border-[var(--border)] rounded divide-y divide-[var(--border)]">
                {preview.direct_reports.map(r => (
                  <li key={r.user_id} className="px-3 py-2 text-[12px] flex justify-between" data-testid={`report-${r.user_id}`}>
                    <span><strong>{r.name}</strong> <span className="text-[var(--text-muted)]">({r.initials})</span></span>
                    <span className="text-[var(--text-muted)]">{r.designation || r.role || "—"} · {r.department || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={runExecute}
              disabled={busy || preview.direct_reports_count === 0}
              data-testid="bulk-reassign-execute"
              className="span-btn h-10 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <ShieldCheck size={14} /> Confirm & reassign {preview.direct_reports_count} report(s)
            </button>
          </div>
        </section>
      )}

      {executed && (
        <section className="card-flat p-5 border border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="bulk-reassign-result">
          <div className="font-bold flex items-center gap-1.5 mb-2">
            <CheckCircle size={16} /> Migration complete · {executed.migrated_count} user(s) reassigned
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <Tile label="Leaves rerouted" value={executed.aggregate.leave_requests_rerouted} testid="agg-leaves" />
            <Tile label="Cases re-stamped" value={executed.aggregate.cases_restamped} testid="agg-cases" />
            <Tile label="Descendants affected" value={executed.aggregate.descendants_affected} testid="agg-desc" />
          </div>
          <details className="text-[12px]">
            <summary className="cursor-pointer font-semibold">Per-user breakdown</summary>
            <ul className="mt-1 pl-4 list-disc">
              {executed.migrated.map(m => (
                <li key={m.user_id}>
                  <strong>{m.name}</strong> ({m.initials}) — {m.propagation?.leave_requests_rerouted || 0} leaves · {m.propagation?.cases_restamped || 0} cases · {m.propagation?.descendants_affected || 0} sub-reports
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}
    </div>
  );
}

function Picker({ label, value, options, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">{label}</label>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="span-input w-full text-[13px] h-10"
      >
        <option value="">{placeholder}</option>
        {options.map(u => (
          <option key={u.user_id} value={u.user_id}>
            {u.name} ({u.initials || "?"}) · {u.designation || u.role || `Tier ${u.ux_tier || "?"}`}
          </option>
        ))}
      </select>
    </div>
  );
}

function Tile({ label, value, testid, mono }) {
  return (
    <div className="border border-[var(--border)] rounded p-2.5 bg-[var(--surface,#fff)]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className={`text-xl font-bold tabular-nums leading-tight ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
